import { h, defineComponent } from 'vue';
import { Button } from '@/ui/components/Button';
import { t } from '@/ui/i18n/i18n';
import { SupportedExportFormat, SupportedExportFormatEnum } from '@/services/api/resources/inspection/exports';
import { isOnFreePlan } from '@/ui/auth/auth';
// @ts-ignore
import { BDropdown, BDropdownItem } from 'buefy/dist/esm/dropdown';
import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
import { StarredAdItem } from '@/ui/components/upgrade/StarredAdItem';
import { inspectionExportTypes } from '@/ui/pages/inspection/export/constants';
import { starsIcon } from '@/ui/components/icon/icons';
import { Icon } from '@/ui/components/icon/Icon';
import { Tooltip } from '@/ui/components/Tooltip';

interface InspectionReportExportProps {
  loading: boolean;
  iconized?: boolean;
  tooltiped?: boolean;
  unlabeled?: boolean;
  formats: Array<SupportedExportFormatEnum>;
}

export const IssueReportExport = defineComponent<InspectionReportExportProps>({
  name: 'InspectionReportExport',
  props: ['loading', 'iconized', 'tooltiped', 'unlabeled', 'formats'],
  setup(props, context) {
    const onClickExport = (format: SupportedExportFormat) => {
      context.emit('export', { format });
    };
    const exportFormatButtonLabelMap: Map<SupportedExportFormatEnum, string> = new Map([
      [SupportedExportFormatEnum.XLSX, inspectionExportTypes.excel],
      [SupportedExportFormatEnum.CSV, inspectionExportTypes.csv],
    ]);

    return function render() {
      const additionalFormats = props.formats
        .filter(format => exportFormatButtonLabelMap.has(format))
        .map(format => {
          return h(
            BDropdownItem,
            { on: { click: () => onClickExport(format) } }, [exportFormatButtonLabelMap.get(format)],
          );
        });

      const starredAdEntry = (text: string) => h(StarredAdItem, {
        class: 'inspection-report-export-ad__entry', props: { isStarred: true },
      }, text);

      //lazy load if not a free plan
      const additionalFormatsAd = () => [
        h(UpgradeAd,
          {
            class: 'upgrade-ad--issue-report-export issue-report-export-ad',
            props: {
              message: t('issues.export-ad'),
              withDropdown: true,
            },
          },
          [
            h(
              'div',
              { class: 'issue-report-export-ad__fields', slot: 'fields' },
              props.formats
                .filter(format => exportFormatButtonLabelMap.has(format))
                .map(format => {
                  return starredAdEntry(exportFormatButtonLabelMap.get(format) || '');
                }),
            ),
          ]),
      ];

      const dropdownTrigger = ({ active }: { active: boolean }) => h('div', [
        h(
          Button, {
            class: `issue-report-export-btn ${props.iconized ? 'issue-report-export-btn--iconized' : ''}`,
            props: { loading: props.loading, type: props.iconized ? 'is-text' : '' },
            attrs: {
              iconRight: active ? 'angle-up' : 'angle-down',
            },
          }, [
            isOnFreePlan.value ? starsIcon() : null,
            h(Icon, {
              props: {
                classes: 'issue-report-export-btn__download-icon',
                icon: 'download',
              },
            }),
            !props.iconized && !props.unlabeled && h('div', [t('common.export')]),
          ],
        ),
      ]);

      const dropdownTriggerWithTooltip = ({ active }: { active: boolean }) => h(Tooltip, {
        props: {
          label: t('report-download.export'),
          position: 'is-top',
        },
      }, [dropdownTrigger({ active })]);

      return h('div', { class: 'issue-report-export' }, [
        h(
          BDropdown,
          {
            class: 'issue-report-export-dropdown',
            attrs: { position: 'is-bottom-left' },
            scopedSlots: {
              trigger: props.tooltiped ? dropdownTriggerWithTooltip : dropdownTrigger,
            },
          },
          [
            ...(isOnFreePlan.value ? additionalFormatsAd() : additionalFormats),
          ],
        )]);
    };
  },
});
